import { useId, useRef, useState, type DragEvent } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { cn } from '@/utils/cn';
import { FieldLabel } from '@/components/ui/FieldLabel';
import { Button } from '@/components/ui/Button';
import type { ButtonVariant } from '@/design-system/tokens';

export type FileInputVariant = 'dropzone' | 'button' | 'compact';

export interface FileInputProps {
  label?: string;
  description?: string;
  error?: string;
  hint?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  loading?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  value?: File | File[] | null;
  placeholder?: string;
  variant?: FileInputVariant;
  buttonLabel?: string;
  buttonVariant?: ButtonVariant;
  onChange?: (files: File[]) => void;
  onClear?: () => void;
}

function normalizeValue(value: File | File[] | null | undefined): File[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFileLabel(file: File): string {
  const name = file.name.length > 48 ? `${file.name.slice(0, 45)}…` : file.name;
  return `${name} · ${formatFileSize(file.size)}`;
}

export function FileInput({
  label,
  description,
  error,
  hint,
  accept,
  multiple = false,
  disabled,
  loading,
  required,
  className,
  id,
  name,
  value,
  placeholder = 'No file chosen',
  variant = 'dropzone',
  buttonLabel = 'Choose file',
  buttonVariant = 'secondary',
  onChange,
  onClear,
}: FileInputProps) {
  const autoId = useId();
  const inputId = id ?? name ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const selected = normalizeValue(value);
  const hasSelection = selected.length > 0;
  const busy = disabled || loading;

  const emitFiles = (list: FileList | null) => {
    if (!list || list.length === 0) {
      onChange?.([]);
      return;
    }
    onChange?.(Array.from(list));
  };

  const openPicker = () => {
    if (!busy) inputRef.current?.click();
  };

  const clearSelection = () => {
    if (inputRef.current) inputRef.current.value = '';
    onClear?.();
    onChange?.([]);
  };

  const onInputChange = () => {
    emitFiles(inputRef.current?.files ?? null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (busy) return;
    emitFiles(event.dataTransfer.files);
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      id={inputId}
      name={name}
      type="file"
      accept={accept}
      multiple={multiple}
      disabled={busy}
      className="sr-only"
      onChange={onInputChange}
    />
  );

  if (variant === 'button') {
    return (
      <div className={cn('inline-flex', className)}>
        {hiddenInput}
        <Button
          type="button"
          variant={buttonVariant}
          loading={loading}
          disabled={disabled}
          onClick={openPicker}
        >
          <UploadFileIcon fontSize="small" />
          {buttonLabel}
        </Button>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className={cn('w-full', className)}>
        <FieldLabel htmlFor={inputId} label={label} required={required} />
        {hiddenInput}
        <div
          className={cn(
            'flex min-h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900',
            error && 'border-red-500',
            busy && 'cursor-not-allowed opacity-60',
          )}
        >
          <InsertDriveFileOutlinedIcon className="shrink-0 text-slate-400" fontSize="small" />
          <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">
            {hasSelection
              ? multiple
                ? `${selected.length} file${selected.length === 1 ? '' : 's'} selected`
                : formatFileLabel(selected[0]!)
              : placeholder}
          </span>
          {hasSelection ? (
            <button
              type="button"
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
              onClick={clearSelection}
              disabled={busy}
              aria-label="Remove file"
            >
              <CloseIcon fontSize="small" />
            </button>
          ) : null}
          <Button type="button" size="sm" variant="secondary" onClick={openPicker} disabled={busy} loading={loading}>
            Browse
          </Button>
        </div>
        {description ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p> : null}
        {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
        {!error && hint ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)}>
      <FieldLabel htmlFor={inputId} label={label} required={required} />
      {hiddenInput}
      <div
        role="button"
        tabIndex={busy ? -1 : 0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragOver(false);
        }}
        onDrop={onDrop}
        onClick={openPicker}
        className={cn(
          'relative rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
          dragOver
            ? 'border-primary-400 bg-primary-50/80 dark:border-primary-500 dark:bg-primary-950/40'
            : 'border-slate-200 bg-slate-50/50 hover:border-primary-300 hover:bg-primary-50/40 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-primary-600',
          error && 'border-red-400 dark:border-red-500',
          busy && 'cursor-not-allowed opacity-60',
          !busy && 'cursor-pointer',
        )}
      >
        <UploadFileIcon
          className="mx-auto mb-2 text-slate-400 dark:text-slate-500"
          fontSize="medium"
        />
        <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {dragOver ? 'Drop file to upload' : 'Drag and drop or click to browse'}
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {description ?? (accept ? `Accepted: ${accept}` : 'Any file type')}
        </p>
        {loading ? (
          <p className="mt-3 text-xs font-medium text-primary-600 dark:text-primary-400">Uploading…</p>
        ) : null}
      </div>

      {hasSelection ? (
        <ul className="mt-3 space-y-2">
          {selected.map((file) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            >
              <InsertDriveFileOutlinedIcon className="shrink-0 text-primary-600 dark:text-primary-400" fontSize="small" />
              <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">
                {formatFileLabel(file)}
              </span>
              <button
                type="button"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
                disabled={busy}
                aria-label={`Remove ${file.name}`}
              >
                <CloseIcon fontSize="small" />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{placeholder}</p>
      )}

      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      {!error && hint ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p> : null}
    </div>
  );
}
